<template>
    <Row type="none" class="bg-white rounded shadow min-h-screen">

        <b-modal :hide-header="true" footer-class="p-2 border-top-0 d-flex justify-content-between" id="delete">

            <template #default="{}">
                <h5 style="text-align: center">{{ $t('customer.hasDebts') }}</h5>
            </template>

            <template #modal-footer="{ ok, cancel }">
                <b-button variant="success" @click="cancel()">
                    {{ $t('cancelDelete') }}
                </b-button>
                <b-button variant="danger" @click="deleteOneCustomer(customerId); ok()">
                    {{ $t('customer.deleteAnyway') }}
                </b-button>
            </template>
        </b-modal>

        <div class="grid grid-cols-12 pt-4 container gap-3">
            <div class="col-span-12 md:col-span-2">
                <ButtonTag
                    :is-tw-main-btn="true"
                    v-if="canManageCustomers()"
                    url="customers/add"
                >
                    <div class="pt-1">
                        {{ $t('customer.add') }}
                    </div>
                </ButtonTag>
            </div>
            <div class="col-span-12 md:col-span-2">
                <ButtonTag
                    :is-tw-main-btn="true"
                    v-if="canManageCustomers()"
                    url="customers/debtors"
                >
                    <div class="pt-1">
                        {{ $t('customer.debtors') }}
                    </div>
                </ButtonTag>
            </div>

            <div class="col-span-4">

            </div>

            <div class="border border-gray-700 rounded h-11 col-span-12 md:col-span-2 position-relative mt-4 mt-md-0">
                <div class="font-bold text-sm bg-white px-1 text-fontColor z-30 position-absolute -top-3 ml-2">
                    {{ $t('customer.numberOfCustomers') }}
                </div>
                <div class="pt-2 pl-3 font-bold text-gray-500">
                    {{ getCustomersTotalCount }}
                </div>
            </div>

            <div class="border border-gray-700 rounded h-11 col-span-12 md:col-span-2 position-relative mt-4 mt-md-0">
                <div class="font-bold text-sm bg-white px-1 text-fontColor z-30 position-absolute -top-3 ml-2">
                    {{ $t('customer.numberOfDebtors') }}
                </div>
                <div class="pt-2 pl-3 font-bold text-gray-500">
                    {{ getDebtorCustomerCount[0] }}
                </div>
            </div>
        </div>

        <div class="container">
            <SearchCollapse class="pt-4" :is-bg-white="true" :do-action="showCheckboxes">
                <div class="grid grid-cols-12 gap-3">
                    <div class="col-span-12 md:col-span-3 position-relative pl-0">
                        <div class="font-bold text-sm bg-white px-1 text-fontColor z-30 position-absolute bottom-10 ml-2">
                           {{ $t('person.givenName') }}
                        </div>
                        <b-form-input
                            v-model="search.givenName"
                            class="focus:ring-0 border-gray-300 h-11"
                            @keyup.enter="searchCustomers"
                        >
                        </b-form-input>
                    </div>

                    <div class="col-span-12 md:col-span-3 position-relative pl-0">
                        <div class="font-bold text-sm bg-white px-1 text-fontColor z-30 position-absolute bottom-10 ml-2">
                            {{ $t('person.familyName') }}
                        </div>
                        <b-form-input
                            v-model="search.familyName"
                            class="focus:ring-0 border-gray-300 h-11"
                            @keyup.enter="searchCustomers"
                        >
                        </b-form-input>
                    </div>

                    <div class="col-span-12 md:col-span-3 position-relative pl-0">
                        <div class="font-bold text-sm bg-white px-1 text-fontColor z-30 position-absolute bottom-10 ml-2">
                            {{ $t('company.name') }}
                        </div>
                        <b-form-input
                            v-model="search.companyName"
                            class="focus:ring-0 border-gray-300 h-11"
                            @keyup.enter="searchCustomers"
                        >
                        </b-form-input>
                    </div>

                    <div class="col-span-12 md:col-span-3">
                        <button class="bg-main border-0 hover:bg-hoverMain rounded-l h-11 w-50 focus:outline-none">
                            <img src="/images/search1.png" alt="" class="h-6 cursor-pointer" @click="searchCustomers">
                        </button>
                        <button class="bg-main border-0 hover:bg-hoverMain rounded-r h-11 w-50 focus:outline-none">
                            <img src="/images/eraser%20(2).png" alt="" class="h-6 cursor-pointer" @click="resetCustomerSearchInfo">
                        </button>
                    </div>
                </div>
            </SearchCollapse>
        </div>

        <RowColumn>
            <TableTag
                class="w-full"
                getter-name="getCustomers"
                fetch-getter-name="fetchAllCustomer"
                :headers="headers"
                :body-properties="bodyProperties"
                :isSelectable=true
            >
                <template v-slot:firstPurchase="row">
                    <span>
                        {{ row.item.firstPurchaseDate ? dateTimeFormat(row.item.firstPurchaseDate) : '—' }}
                    </span>
                </template>
                <template v-slot:custom="row">
                    <router-link :to="$i18nHelper.getUrl('customers/' + row.item.id)" class="flex justify-start">
                        <div v-if="row.item.isCompany">
                            <font-awesome-icon
                                class="awesome-icon-amount"
                                :icon="['fa', 'building']"
                                size="1x"
                            />
                        </div>
                        <div v-if="!row.item.isCompany">
                            <font-awesome-icon
                                class="awesome-icon-amount"
                                :icon="['fa', 'user-tie']"
                                size="1x"
                            />
                        </div>
                        <div class="pl-3">
                            {{
                                row.item.isCompany ? row.item.customerCompany.name : row.item.person.givenName + ' ' + row.item.person.familyName
                            }}
                        </div>
                    </router-link>
                </template>
                <template v-slot:telephone="row">
                    <span v-if="row.item.isCompany">
                        <span v-if="row.item.customerCompany.telephone !== null">{{
                                row.item.customerCompany.telephone
                            }}</span>
                    </span>
                    <span v-else>
                        <span v-if="row.item.person.telephone !== null">{{ row.item.person.telephone }}</span>
                    </span>
                </template>
                <template v-slot:averageCheck="row">
                    <span>
                        {{ showCurrencyById(getCurrencyByShortName(currentCurrencyShortName).id, row.item.avgReceipt) }}
                    </span>
                </template>
                <template v-slot:actions="row">
                    <span class="d-flex">
                        <img src="/images/edit.png" alt="" class="h-4 mt-1 cursor-pointer pr-2" @click="$i18nHelper.toUrl('customers/' + row.item.id)">

                        <img src="/images/read-more.png" alt="" class="h-6 cursor-pointer pr-3" @click="$i18nHelper.toUrl('customers/view/' + row.item.id)">

                        <img src="/images/Union.png" alt="" class="h-5 mt-0.5 cursor-pointer" @click="$bvModal.show('delete-customer-' + row.item.id)">
                    </span>
                    <Modal
                        text="deleteConfirm"
                        ok-text="yes"
                        cancel-text="no"
                        :is-ok-only="false"
                        :context="{name: row.item.isCompany ? row.item.customerCompany.name : row.item.person.givenName + ' ' + row.item.person.familyName}"
                        :id="'delete-customer-' + row.item.id"
                        :on-ok-pressed="() => {deleteOneCustomer(row.item.id)}"
                    />
                </template>
                <template v-slot:checkboxes="row" v-if="canSeeCustomerDebts()">
                    <b-form-checkbox
                        class="-mr-2"
                        v-if="isShowCheckbox"
                        size="lg"
                        @change="getId(row.item)"
                        :value=row.item.id
                        v-model="selectedCustomers"
                    />
                </template>
                <template v-slot:checkAll_0 v-if="canSeeCustomerDebts()">
                    <b-form-checkbox
                        class="-mr-2"
                        v-if="isShowCheckbox"
                        size="lg"
                        @change="selectAll"
                        v-model="isSelectedAll"
                    />
                </template>
            </TableTag>
            <div>
                <b-button
                    v-if="selectedCustomers.length > 0"
                    class="bg-denyColor hover:bg-hoverDenyColor rounded h-11 border-0 float-right mt-4 mr-4"
                    @click="showModal">
                    {{ $t('delete') }}
                </b-button>
            </div>
            <div class="clear">
            </div>

            <b-modal id="delete-customer-modal" hide-footer :title="$t('warning')">
                <!-- Modal body -->
                <div class="modal-body">
                    {{ $t('customer.deleteCustomWarning') }}
                </div>
                <!-- Modal footer -->
                <div class="modal-footer">
                    <button type="button" class="bg-denyColor w-32 font-bold text-white hover:bg-hoverDenyColor rounded h-11 border-0" data-dismiss="modal" @click="deleteCustomers">
                        {{ $t('yes') }}
                    </button>
                    <button type="button" class="bg-main w-32 font-bold text-white hover:bg-hoverMain rounded h-11 border-0" data-dismiss="modal" @click="cancelDelete">
                        {{ $t('no') }}
                    </button>
                </div>
            </b-modal>
        </RowColumn>

        <div class="container mt-4">
            <PaginationRow
                :search="search"
                base-url="customer"
                method-name="fetchAllCustomer"
                :total-items="getCustomersTotalCount"
            />
        </div>
    </Row>
</template>

<script>
import Row from "../../components/html/Row";
import RowColumn from "../../components/RowColumn";
import {mapActions, mapGetters} from "vuex";
import ButtonTag from "../../components/html/ButtonTag";
import TableTag from "../../components/html/TableTag";
import PaginationRow from "@/components/PaginationRow";
import accessMixin from "@/mixin/accessMixin";
import Modal from "../../components/html/Modal";
import currencyFormatMixin from "@/mixin/currencyFormatMixin";
import dateFormattingMixin from "../../mixin/dateFormattingMixin";
import SearchCollapse from "@/components/SearchCollapse.vue";

export default {
    name: "List",
    components: {
        TableTag,
        ButtonTag,
        RowColumn,
        Row,
        PaginationRow,
        Modal,
        SearchCollapse
    },
    mixins: [accessMixin, currencyFormatMixin, dateFormattingMixin],
    data() {
        return {
            customerId: 0,
            breadcrumbItems: [
                {name: 'home', url: ''},
                {name: 'customer.title'},
            ],
            search: {
                givenName: '',
                familyName: '',
                companyName: '',
                page: 1
            },
            headers: [
                {name: 'id', hideOnMobile: true},
                {name: 'customer.firstPurchase', hideOnMobile: true},
                {name: 'name'},
                {name: 'company.telephone'},
                {name: 'customer.averageCheck', hideOnMobile: true},
                {name: 'actions'}
            ],
            bodyProperties: [
                {
                    name: 'id', bgWarning: function () {}, bgDanger: function () {}, hideOnMobile: true
                },
                {
                    name: 'firstPurchase', bgWarning: function () {}, bgDanger: function () {}, hideOnMobile: true
                },
                {
                    name: 'custom', bgWarning: function () {}, bgDanger: function () {},
                },
                {
                    name: 'telephone', bgWarning: function () {}, bgDanger: function () {},
                },
                {
                    name: 'averageCheck', bgWarning: function () {}, bgDanger: function () {}, hideOnMobile: true
                },
                {
                    name: 'actions', bgWarning: function () {}, bgDanger: function () {},
                }
            ],
            isShowCheckbox: false,
            selectedCustomers: [],
            isCheckedAll: false,
            isSelectedAll: false,
        }
    },
    computed: {
        ...mapGetters([
            'getCompanyRemaining',
            'getCustomer',
            'getCustomersTotalCount',
            'getDebt',
            'getCustomers',
            "getUserCompany",
            "getSale",
            "getCustomerSalesTotalAmount",
            "getSalesTotalCount",
            "getCustomerFirstPurchase",
            "getCurrencyByShortName",
            'getDebtorCustomerCount'
        ])
    },
    methods: {
        ...mapActions([
            'deleteCustomer',
            'searchCustomer',
            'fetchDebt',
            'fetchCustomers',
            'fetchCompanyRemaining',
            'fetchUser',
            'fetchSale',
            'fetchCustomerSaleFirstPurchase',
            'fetchDebtorCustomerCount',
            'setBreadcrumbItems',
            'fetchAllCustomer',
            'fetchAllCustomers'
        ]),
        showModal() {
            if (this.selectedCustomers.length > 0) {
                this.$bvModal.show('delete-customer-modal')
            }
        },
        showCheckboxes() {
            if (this.isShowCheckbox) {
                this.headers.shift()
                this.bodyProperties.shift()
            } else {
                this.headers.unshift({name: ''})
                this.bodyProperties.unshift({
                    name: 'checkboxes',
                    bgWarning: this.isBgColorWarning,
                    bgDanger: function () {
                    }
                })
            }

            this.isShowCheckbox = !this.isShowCheckbox

            if (this.selectedCustomers.length > 0) {
                this.selectedCustomers = []
                this.isCheckedAll = false
                this.isSelectedAll = false
            }
        },
        getId(custom) {
            let hasCustomId = this.selectedCustomers.some(function (item) {
                return item === custom.id
            })

            if (hasCustomId) {
                let index = this.selectedCustomers.indexOf(custom.id)
                this.selectedCustomers.splice(index, 1)
            } else {
                this.selectedCustomers.push(custom.id)
            }
        },
        async deleteCustomersAction() {
            this.selectedCustomers.forEach(sale => {
                this.modalOk(sale)
            })
        },
        deleteCustomers() {
            this.$root.showOverlay = true

            this.deleteCustomersAction()
                .then(() => {
                    this.selectedCustomers = []
                    this.$bvModal.hide('delete-customer-modal')
                    this.isCheckedAll = false
                })
                .finally(() => {
                    setTimeout(this.updateCustomers, 1000)
                })
        },
        updateCustomers() {
            this.fetchAllCustomer(this.search)
            this.$root.showOverlay = false
        },
        cancelDelete() {
            this.$bvModal.hide('delete-customer-modal')
            this.selectedCustomers = []
            this.isCheckedAll = false
        },
        selectAll() {
            if (this.isSelectedAll) {
                this.selectedCustomers = []
                this.isSelectedAll = false
            } else {
                this.getCustomers.forEach(custom => {
                    this.selectedCustomers.push(custom.id)
                })

                this.isSelectedAll = true
            }
        },
        isBgColorWarning(item) {
            return this.hasDebts(item)
        },
        hasDebts(item) {
            return item.totalDebt !== 0 || item.debt > 0
        },
        getItemName(item) {
            return item.isCompany ? item.customerCompany.name : item.person.givenName + ' ' + item.person.familyName
        },
        getCustomerName(item) {
            return item.customer.isCompany ?
                item.customer.customerCompany.name :
                item.customer.person.givenName + ' ' + item.customer.person.familyName
        },
        deleteOneCustomer(id) {
            this.$root.showOverlay = true

            this.deleteCustomer(id)
                .then(() => {
                    this.fetchAllCustomer(this.search)
                        .then(() => {
                            this.$root.showOverlay = false
                        })
                })
        },
        modalOk(id) {
            this.$root.showOverlay = true

            this.fetchDebt(id)
                .then(() => {
                    if (this.getDebt.customerDebt > 0) {
                        this.customerId = id
                        this.$bvModal.show('delete')
                        this.$root.showOverlay = false
                    } else {
                        this.deleteOneCustomer(id)
                    }
                })
        },
        searchCustomers() {
            this.$root.showOverlay = true
            this.searchCustomer(this.search)
                .catch((err) => {
                    console.log(err)
                })
                .finally(() => {
                    this.$root.showOverlay = false
                });
        },
        resetCustomerSearchInfo() {
            Object.assign(this.$data, this.$options.data.call(this));
            this.searchCustomers()
        }
    },
    created() {
        this.setBreadcrumbItems(this.breadcrumbItems)
    },
    async mounted() {
        if (this.getUserCompany.id === null) {
            await this.fetchUser()
        }
        await this.fetchCompanyRemaining()
        this.fetchDebtorCustomerCount()
        this.fetchSale(this.$route.params.id)
    },
}
</script>
<style scoped>
.form-control {
    margin-bottom: 5px !important;
}

.btn-danger {
    background-color: red;
}

.mb-3 {
    margin-bottom: -16px !important;
}

@media (min-width: 576px) {
    .mr-md-1 {
        margin-right: 0.25rem !important;
    }
}
</style>
